import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Search, Eye, RefreshCw } from "lucide-react";
import { recalculateProgressConsolidationRequest } from "../../../redux/actions/performanceActions";

export default function PerformanceCollaborateursTable({ collaborateurs = [], onViewDetail }) {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [managerFilter, setManagerFilter] = useState("ALL");
    const [entityFilter, setEntityFilter] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const handleRecalculate = (progressId, e) => {
        e.stopPropagation();
        if (progressId) {
            dispatch(recalculateProgressConsolidationRequest(progressId));
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "NOT_STARTED": return "Non démarré";
            case "IN_PROGRESS": return "En cours";
            case "PENDING_VALIDATION": return "En attente validation";
            case "COMPLETED": return "Terminé";
            default: return status || "Inconnu";
        }
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return "--";
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return "--";
        return `${numericValue.toFixed(2)}%`;
    };

    // Extract unique managers and entities from data for filter select options
    const uniqueManagers = useMemo(() => {
        const managers = {};
        collaborateurs.forEach(c => {
            if (c.managerId && c.managerNom) {
                managers[c.managerId] = c.managerNom;
            }
        });
        return Object.entries(managers).map(([id, name]) => ({ id, name }));
    }, [collaborateurs]);

    const uniqueEntities = useMemo(() => {
        const entities = {};
        collaborateurs.forEach(c => {
            if (c.entityId && c.entityName) {
                entities[c.entityId] = c.entityName;
            }
        });
        return Object.entries(entities).map(([id, name]) => ({ id, name }));
    }, [collaborateurs]);

    // Apply filtering and search
    const filteredList = useMemo(() => {
        return collaborateurs.filter(c => {
            const matchesSearch =
                c.collaborateurNom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.managerNom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.entityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.jobName?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = statusFilter === "ALL" || c.globalStatus === statusFilter;
            const matchesManager = managerFilter === "ALL" || String(c.managerId) === managerFilter;
            const matchesEntity = entityFilter === "ALL" || String(c.entityId) === entityFilter;

            return matchesSearch && matchesStatus && matchesManager && matchesEntity;
        });
    }, [collaborateurs, searchTerm, statusFilter, managerFilter, entityFilter]);

    // Apply pagination
    const totalItems = filteredList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedList = useMemo(() => {
        const start = (safePage - 1) * pageSize;
        return filteredList.slice(start, start + pageSize);
    }, [filteredList, safePage, pageSize]);

    return (
        <div className="perf-table-container">
            {/* Search and filter Toolbar */}
            <div className="perf-cons-toolbar">
                <div className="perf-cons-search-container">
                    <Search size={16} className="perf-cons-search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher par collaborateur, manager, poste..."
                        className="perf-cons-search-input"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <select
                    className="perf-cons-filter-select"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                    <option value="ALL">Tous les statuts</option>
                    <option value="NOT_STARTED">Non démarré</option>
                    <option value="IN_PROGRESS">En cours</option>
                    <option value="PENDING_VALIDATION">En attente validation</option>
                    <option value="COMPLETED">Terminé</option>
                </select>

                <select
                    className="perf-cons-filter-select"
                    value={managerFilter}
                    onChange={(e) => { setManagerFilter(e.target.value); setCurrentPage(1); }}
                >
                    <option value="ALL">Tous les managers</option>
                    {uniqueManagers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>

                <select
                    className="perf-cons-filter-select"
                    value={entityFilter}
                    onChange={(e) => { setEntityFilter(e.target.value); setCurrentPage(1); }}
                >
                    <option value="ALL">Toutes les entités</option>
                    {uniqueEntities.map(ent => (
                        <option key={ent.id} value={ent.id}>{ent.name}</option>
                    ))}
                </select>

                <select
                    className="perf-cons-filter-select"
                    style={{ minWidth: "80px" }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                >
                    <option value={5}>5 par page</option>
                    <option value={10}>10 par page</option>
                    <option value={20}>20 par page</option>
                </select>
            </div>

            {/* Table */}
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                    <tr>
                        <th>Collaborateur</th>
                        <th>Poste</th>
                        <th>Manager</th>
                        <th>Entité</th>
                        <th>Statut Global</th>
                        <th>Progression</th>
                        <th>Score Global</th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {paginatedList.length === 0 ? (
                        <tr>
                            <td colSpan="8" className="perf-state" style={{ textAlign: "center", padding: "30px" }}>
                                Aucun collaborateur ne correspond aux critères de recherche.
                            </td>
                        </tr>
                    ) : (
                        paginatedList.map(c => (
                            <tr key={c.collaborateurId}>
                                <td style={{ fontWeight: "700", color: "#2f4595" }}>{c.collaborateurNom}</td>
                                <td>{c.jobName || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                                <td>{c.managerNom || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                                <td>{c.entityName || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                                <td>
                                        <span className={`status-badge ${c.globalStatus}`}>
                                            {getStatusText(c.globalStatus)}
                                        </span>
                                </td>
                                <td>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontSize: "11px", fontWeight: "700", minWidth: "30px" }}>
                                                {parseFloat(c.progressionPourcentage || 0).toFixed(0)}%
                                            </span>
                                        <div className="perf-progressbar" style={{ width: "60px" }}>
                                            <span style={{ width: `${c.progressionPourcentage || 0}%` }}></span>
                                        </div>
                                    </div>
                                </td>
                                <td style={{ fontWeight: "800", color: "#2f4595" }}>
                                    {formatPercent(c.scoreGlobal)}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    <div style={{ display: "inline-flex", gap: "6px" }}>
                                        <button
                                            className="perf-btn"
                                            style={{ padding: "5px 8px" }}
                                            onClick={() => onViewDetail(c.evaluationProgressId)}
                                            title="Voir détails"
                                        >
                                            <Eye size={13} />
                                        </button>
                                        <button
                                            className="perf-btn"
                                            style={{ padding: "5px 8px" }}
                                            onClick={(e) => handleRecalculate(c.evaluationProgressId, e)}
                                            title="Recalculer"
                                        >
                                            <RefreshCw size={13} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="perf-cons-pagination">
                    <button
                        className="perf-cons-page-btn"
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                    >
                        Précédent
                    </button>
                    <span className="perf-cons-page-info">
                        Page <strong>{safePage}</strong> sur <strong>{totalPages}</strong> ({totalItems} collaborateurs)
                    </span>
                    <button
                        className="perf-cons-page-btn"
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
}
